/** Builds and sends the request the panel has been configured with. */

import type { TryItParam, TryItSpec } from './spec.ts';

export interface RequestValues {
  params: Record<string, string>;
  /** Optional query params the reader has switched on. */
  enabled: Record<string, boolean>;
  body: string;
}

export interface BuiltRequest {
  url: string;
  method: string;
  headers: Record<string, string>;
  body?: string;
  /** Unfilled required fields, so the panel can point at them instead of sending. */
  missing: string[];
}

function include(param: TryItParam, values: RequestValues): boolean {
  if (param.required) return true;
  if (!values.enabled[param.name]) return false;
  return (values.params[param.name] ?? '').trim() !== '';
}

export function buildRequest(
  spec: TryItSpec,
  values: RequestValues,
  token: string | null,
): BuiltRequest {
  const missing: string[] = [];
  let path = spec.path;

  for (const param of spec.params.filter((p) => p.in === 'path')) {
    const value = (values.params[param.name] ?? '').trim();
    if (!value) missing.push(param.name);
    // encodeURIComponent, not raw: an id with a slash would otherwise rewrite the route.
    path = path.replace(`{${param.name}}`, encodeURIComponent(value));
  }

  const query = new URLSearchParams();
  for (const param of spec.params.filter((p) => p.in === 'query')) {
    if (!include(param, values)) {
      if (param.required) missing.push(param.name);
      continue;
    }
    const value = (values.params[param.name] ?? '').trim();
    if (param.required && !value) missing.push(param.name);
    query.set(param.name, value);
  }

  const headers: Record<string, string> = { Accept: 'application/json' };
  for (const param of spec.params.filter((p) => p.in === 'header')) {
    if (!include(param, values)) {
      if (param.required) missing.push(param.name);
      continue;
    }
    headers[param.name] = (values.params[param.name] ?? '').trim();
  }

  if (spec.requiresAuth && token) headers.Authorization = `Bearer ${token}`;

  let body: string | undefined;
  if (spec.body && values.body.trim()) {
    body = values.body;
    headers['Content-Type'] = spec.body.contentType;
  }

  const qs = query.toString();
  return {
    url: `${spec.server}${path}${qs ? `?${qs}` : ''}`,
    method: spec.method,
    headers,
    body,
    missing: [...new Set(missing)],
  };
}

export interface ResponseResult {
  ok: boolean;
  status: number;
  statusText: string;
  durationMs: number;
  bytes: number;
  headers: [string, string][];
  text: string;
  json: unknown;
  /** Set when the request never reached the server at all. */
  networkError?: string;
}

export async function sendRequest(request: BuiltRequest): Promise<ResponseResult> {
  const started = performance.now();
  try {
    const response = await fetch(request.url, {
      method: request.method,
      headers: request.headers,
      body: request.body,
      // No cookies: the bearer token is the only credential these hosts accept, and
      // asking for cookies would force credentialed CORS on every response.
      credentials: 'omit',
      mode: 'cors',
    });

    const text = await response.text();
    let json: unknown;
    try {
      json = text ? JSON.parse(text) : undefined;
    } catch {
      json = undefined;
    }

    return {
      ok: response.ok,
      status: response.status,
      statusText: response.statusText,
      durationMs: Math.round(performance.now() - started),
      bytes: new Blob([text]).size,
      headers: [...response.headers.entries()],
      text,
      json,
    };
  } catch (error) {
    // fetch rejects for DNS, offline and CORS alike, and the browser deliberately hides
    // which. Say so plainly rather than guessing at a cause.
    return {
      ok: false,
      status: 0,
      statusText: 'Request failed',
      durationMs: Math.round(performance.now() - started),
      bytes: 0,
      headers: [],
      text: '',
      json: undefined,
      networkError:
        error instanceof Error && error.message ? error.message : 'The request did not complete.',
    };
  }
}

/** The equivalent curl, so a reader can take the request they just built elsewhere. */
export function asCurl(request: BuiltRequest, tokenPlaceholder: string): string {
  const lines = [`curl -X ${request.method} '${request.url}'`];
  for (const [key, value] of Object.entries(request.headers)) {
    const shown = key.toLowerCase() === 'authorization' ? `Bearer ${tokenPlaceholder}` : value;
    lines.push(`  -H '${key}: ${shown}'`);
  }
  if (request.body) lines.push(`  -d '${request.body.replace(/\n\s*/g, '')}'`);
  return lines.join(' \\\n');
}
